// หน้าความจุบัญชี (/admin/bank-capacity) — เพดานตามตั้งค่า (what-if แก้ได้ ไม่บันทึก) vs order จริงรายวันต่อบัญชีฝาก
// ข้อมูล: useBankCapacity (GET /admin/banks + /admin/banks/capacity-report) · แถว what-if: useWhatIfRows · สูตร: capacityMath
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGaugeHigh, faCalendarAlt, faPlus, faRotateLeft, faChartBar, faClock, faClipboardList, faBolt, faCalendarDay,
} from '@fortawesome/free-solid-svg-icons';
import {
  UiPageHeader, UiFilterPanel, UiFilterField, FILTER_INPUT_CLASS, UiStatsGrid, UiButton, UiAlert,
} from '../../../components/ui';
import type { StatCardDef } from '../../../components/ui';
import { todayBangkok } from '../../../hooks/useDateRangeFilter';
import { fmtNum } from '../../../utils/formatMoney';
import { useBankCapacity } from './useBankCapacity';
import { useWhatIfRows } from './useWhatIfRows';
import {
  capacityFor, sumCapacity, peakInWindow, peakHour, sumHourly, windowStatus, windowUsagePct, fmtCap,
} from './capacityMath';
import CapacityTable from './CapacityTable';
import type { ComputedRow } from './CapacityTable';
import HourlyChart from './HourlyChart';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const PRESET_BTN_CLASS =
  'px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-500 text-gray-600 dark:text-slate-300 transition-colors';
const CARD_CLASS = 'bg-white dark:bg-slate-800 p-4 rounded-xl shadow-theme';
const CARD_TITLE_CLASS =
  'text-sm font-bold text-gray-700 dark:text-slate-200 mb-3 pb-2 border-b border-gray-100 dark:border-slate-700 flex items-center gap-2';

const yesterdayBangkok = () => dayjs().tz(TZ).subtract(1, 'day').format('YYYY-MM-DD');

const BankCapacityPage: React.FC = () => {
  const [draftDate, setDraftDate] = useState(todayBangkok);
  const [appliedDate, setAppliedDate] = useState(todayBangkok);

  const {
    banks, windowSupported, actual, allActual, reportSupported, loading, loadedAt, load, // truncated: พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง)
  } = useBankCapacity();
  const { rows, dirty, updateRow, addHypo, removeHypo, resetToReal } = useWhatIfRows(banks);

  useEffect(() => { load(appliedDate); }, [appliedDate, load]);

  const applyDate = (d: string) => { setDraftDate(d); setAppliedDate(d); };
  const handleSearch = () => {
    if (!draftDate) return;
    if (draftDate === appliedDate) load(appliedDate);
    else setAppliedDate(draftDate);
  };
  const handleReset = () => applyDate(todayBangkok());

  // ─── คำนวณสดจากแถว what-if + ของจริง ────────────────────────────────────────
  const computed: ComputedRow[] = useMemo(() => rows.map((row) => {
    const act = row.bankId ? actual[row.bankId] : undefined;
    const peak = act ? peakInWindow(act.created_ms, row.s) : null;
    return {
      row,
      cap: capacityFor(row),
      actual: act,
      peak,
      usagePct: peak ? windowUsagePct(peak.count, row.n) : null,
      status: peak ? windowStatus(peak.count, row.n) : null,
      hourPeak: act ? peakHour(act.hourly) : null,
    };
  }), [rows, actual]);

  const totals = useMemo(() => sumCapacity(computed.map((c) => c.cap)), [computed]);
  // ยอดจริงทั้งวันใช้ทุกแถวจาก RPC (รวมบัญชีที่ปิด/ถอนไปแล้วแต่ยังมี order วันนั้น)
  const hourlyTotal = useMemo(() => sumHourly(allActual.map((a) => a.hourly)), [allActual]);
  const dayTotals = useMemo(
    () => allActual.reduce(
      (acc, a) => ({ total: acc.total + a.total, paid: acc.paid + a.paid, closed: acc.closed + a.expired + a.cancelled }),
      { total: 0, paid: 0, closed: 0 },
    ),
    [allActual],
  );
  const tableOrders = computed.reduce((s, c) => s + (c.actual?.total || 0), 0);
  const outsideOrders = dayTotals.total - tableOrders;
  const dayPeak = useMemo(() => peakHour(hourlyTotal), [hourlyTotal]);

  const isToday = appliedDate === todayBangkok();
  const nowHour = dayjs().tz(TZ).hour();
  const hypoCount = rows.filter((r) => r.kind === 'hypo').length;
  const realCount = rows.length - hypoCount;
  const dateLabel = isToday
    ? `วันนี้${loadedAt ? ` (ถึง ${dayjs(loadedAt).tz(TZ).format('HH:mm')})` : ''}`
    : dayjs(appliedDate).format('DD/MM/YYYY');
  const bankCountLabel = `${realCount} บัญชี${hypoCount > 0 ? ` · สมมุติ ${hypoCount}` : ''}${dirty ? ' · แก้ค่าอยู่' : ''}`;

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faBolt,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'รับได้สูงสุด / นาที',
      value: <span className="text-blue-600 dark:text-blue-400">{fmtCap(totals.perMin)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{bankCountLabel}</span>,
    },
    {
      icon: faClock,
      iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
      label: 'รับได้สูงสุด / ชั่วโมง',
      value: <span className="text-teal-600 dark:text-teal-400">{fmtCap(totals.perHour)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">N × ⌈3600/S⌉ ตัดด้วยเพดานรายวัน</span>,
    },
    {
      icon: faCalendarDay,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'รับได้สูงสุด / วัน',
      value: <span className="text-emerald-600 dark:text-emerald-400">{fmtCap(totals.perDay)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รวมทุกบัญชีในตาราง</span>,
    },
    {
      icon: faClipboardList,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: `ออเดอร์จริง ${dateLabel}`,
      value: reportSupported
        ? <span className="text-amber-600 dark:text-amber-400">{fmtNum(dayTotals.total)}</span>
        : <span className="text-gray-300 dark:text-slate-600">—</span>,
      subValue: reportSupported ? (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          สำเร็จ {fmtNum(dayTotals.paid)} · หมดอายุ/ยกเลิก {fmtNum(dayTotals.closed)}
          {dayPeak ? ` · พีค ${String(dayPeak.hour).padStart(2, '0')}:00 (${fmtNum(dayPeak.count)})` : ''}
        </span>
      ) : <span className="text-xs text-gray-400 dark:text-slate-500">ไม่มีข้อมูลของจริง</span>,
    },
  ], [totals, bankCountLabel, dateLabel, reportSupported, dayTotals, dayPeak]);

  const presets = [
    { label: 'วันนี้', date: todayBangkok() },
    { label: 'เมื่อวาน', date: yesterdayBangkok() },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faGaugeHigh}
        title="ความจุบัญชี"
        subtitle="เพดานตามตั้งค่า vs ออเดอร์จริงรายวัน — แก้ค่าในหน้านี้เพื่อลองสมมุติ ไม่บันทึกลงระบบ"
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={(
          <>
            <UiButton variant="secondary" icon={faPlus} onClick={addHypo}>เพิ่มบัญชีสมมุติ</UiButton>
            <UiButton variant="secondary" icon={faRotateLeft} onClick={resetToReal} disabled={!dirty}>รีเซ็ตเป็นค่าจริง</UiButton>
          </>
        )}
      >
        <UiFilterField label="วันที่ (ของจริง)" icon={faCalendarAlt} className="w-full sm:w-[168px]">
          <input
            type="date"
            value={draftDate}
            max={todayBangkok()}
            onChange={(e) => setDraftDate(e.target.value)}
            className={FILTER_INPUT_CLASS}
          />
        </UiFilterField>
        <div className="flex gap-2 pb-0.5">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyDate(p.date)}
              className={`${PRESET_BTN_CLASS} ${appliedDate === p.date ? 'ring-2 ring-gray-300 dark:ring-slate-500' : ''}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </UiFilterPanel>

      {!reportSupported && !loading && (
        <UiAlert tone="gray" className="mb-4">
          tenant นี้ยังไม่มี RPC รายงานความจุ (migration 022) — แสดงเฉพาะความจุตามตั้งค่า ส่วนออเดอร์จริง/กราฟจะขึ้นหลังลง migration
        </UiAlert>
      )}
      {!windowSupported && banks.length > 0 && (
        <UiAlert tone="amber" className="mb-4">
          tenant นี้ยังไม่เปิดเพดานช่วงเวลา — ช่อง N/S แก้ไม่ได้ ความจุคิดจากเพดานรายวันอย่างเดียว
        </UiAlert>
      )}
      {/* พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง) — alert เมื่อ RPC ตัดที่ 20,000 ใบ (ต้องดึง truncated จาก useBankCapacity กลับมาด้วย)
      {truncated && (
        <UiAlert tone="amber" className="mb-4">
          บางบัญชีมีออเดอร์เกิน 20,000 รายการในวันนี้ — ค่าพีคในหน้าต่างคำนวณจาก 20,000 รายการแรก อาจต่ำกว่าความจริง
        </UiAlert>
      )}
      */}

      <UiStatsGrid cards={statCards} cols={4} loading={loading && rows.length === 0} />

      {reportSupported && (
        <div className={`${CARD_CLASS} mb-6`}>
          <h2 className={CARD_TITLE_CLASS}>
            <FontAwesomeIcon icon={faChartBar} className="text-blue-500 text-xs" />
            ออเดอร์รายชั่วโมง {dateLabel} เทียบความจุ/ชม. ตามค่าในตาราง
            {outsideOrders > 0 && (
              <span className="font-normal text-xs text-gray-400 dark:text-slate-500">
                (รวมบัญชีที่ปิด/ถอนแล้วอีก {fmtNum(outsideOrders)} รายการ)
              </span>
            )}
          </h2>
          <HourlyChart
            hourly={hourlyTotal}
            capacityPerHour={totals.perHour}
            isToday={isToday}
            nowHour={nowHour}
            peakHour={dayPeak?.hour ?? null}
          />
        </div>
      )}

      <div className="mb-6">
        <CapacityTable
          rows={computed}
          totals={totals}
          totalOrders={tableOrders}
          reportSupported={reportSupported}
          windowSupported={windowSupported}
          onChange={updateRow}
          onRemove={removeHypo}
        />
      </div>

      <div className={CARD_CLASS}>
        <h2 className={CARD_TITLE_CLASS}>
          <FontAwesomeIcon icon={faGaugeHigh} className="text-gray-400 text-xs" />
          วิธีคิด
        </h2>
        <ul className="text-xs text-gray-500 dark:text-slate-400 space-y-1 list-disc pl-5">
          <li>ภายใน T วินาที หน้าต่างเลื่อน N รายการ / S วินาที รับได้สูงสุด <span className="font-mono">N × ⌈T/S⌉</span> แล้วไม่เกินเพดานรายวัน (ถ้าเปิด) — ทุกเพดานที่เปิดต้องผ่านพร้อมกัน</li>
          <li>N = 0 คือปิดเพดานช่วงเวลา · เพดานรายวัน 0 = ไม่จำกัด · ปิดทั้งคู่ = ไม่จำกัด</li>
          <li>"ออเดอร์จริง" นับทุกใบที่สร้างในวันนั้น (ทุกสถานะ — ตอนระบบตรวจเพดาน ใบนั้นยังเป็น pending) รวม P2P fallback ที่ยังรอชำระ</li>
          {/* พีคในหน้าต่าง S — เก็บไว้ก่อน (ยังไม่แสดง)
          <li>"พีคในหน้าต่าง S" = จำนวนมากสุดที่เคยเกิดภายใน S วินาทีใด ๆ ของวันนั้น เทียบกับ N ที่ตั้ง — ถึง 100% แปลว่าค่านั้นเคยทำให้บัญชีหยุดรับชั่วคราว</li>
          */}
          <li>ค่าที่แก้ในหน้านี้ไม่ถูกบันทึก — ตั้งค่าจริงที่หน้า Banks</li>
        </ul>
      </div>
    </div>
  );
};

export default BankCapacityPage;
